import Button from '@platform-ui/button-latest';
import Int24Trash from '@platform-ui/iconsPack/interface/24/Trash';
import Textarea from '@platform-ui/textarea-latest';
import Tooltip from '@platform-ui/tooltip-latest';
import cn from 'classnames';
import React from 'react';
import TrashIcon from '../../../assets/icons/trash-w.svg';
import intl from '../../../locale';
import styles from './ClickMarker.module.css';
import type { ImageClick } from './types';

interface ClickMarkerProps {
  click: ImageClick;
  readOnly: boolean;
  onDelete: VoidFunction;
  ariaLabel?: string;
}

export const ClickMarker: React.FC<ClickMarkerProps> = ({ click, onDelete, readOnly, ariaLabel }) => {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={{ left: `${click.left}%`, top: `${click.top}%` }}
      onClick={event => {
        event.stopPropagation();
      }}
      onPointerDown={event => {
        event.stopPropagation();
      }}
      className={styles.container}
    >
      <Tooltip
        popoverContent={
          <div>
            <Button disabled={readOnly} theme="flat" size="m" leftIcon={<Int24Trash />} onClick={onDelete}>
              {intl.formatMessage({ id: 'System.DeleteButtonClickMap' })}
            </Button>
          </div>
        }
        withCorner={false}
        customPadding={20}
        zIndex={1000}
        inline
      >
        <div className={styles.commentContainer} aria-label={ariaLabel}>
          <div className={cn(styles.click)} />
        </div>
      </Tooltip>
    </div>
  );
};

interface CommentClickMarkerProps extends ClickMarkerProps {
  active: boolean;
  commentLimit: number;
  commentLabel: string;
  onClick: VoidFunction;
  onSave: VoidFunction;
  onChange: (comment: string) => void;
}

export const CommentClickMarker: React.FC<CommentClickMarkerProps> = ({
  active,
  click,
  onDelete,
  onChange,
  onClick,
  onSave,
  commentLimit,
  commentLabel,
  readOnly,
  ariaLabel,
}) => {
  const { left, top, comment } = click;

  const handleCommentChange = (_: unknown, { value }: { value: string }) => {
    onChange(value);
  };

  const handleClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    if (!readOnly) {
      onClick();
    }
  };

  const isSaveDisabled = !comment || (!!commentLimit && comment.length > commentLimit);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      style={{ left: `${left}%`, top: `${top}%` }}
      onClick={handleClick}
      className={styles.container}
      onPointerDown={event => {
        event.stopPropagation();
      }}
    >
      <Tooltip
        key={`${active}`}
        customPadding={20}
        zIndex={1000}
        popoverContent={
          active ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              onClick={event => {
                event.stopPropagation();
              }}
            >
              <Textarea
                size="m"
                minRows={3}
                value={comment}
                label={commentLabel}
                maxLength={commentLimit}
                onChange={handleCommentChange}
              />

              <div className={styles.buttonGroup}>
                <Button
                  ariaLabel="Удалить комментарий"
                  theme="secondary"
                  size="s"
                  leftIcon={<TrashIcon />}
                  onClick={onDelete}
                />

                <Button size="s" theme="accent" onClick={onSave} disabled={isSaveDisabled} wide>
                  {intl.formatMessage({ id: 'System.SaveButton' })}
                </Button>
              </div>
            </div>
          ) : (
            <span>{comment}</span>
          )
        }
        isActive={active ? active : undefined}
        direction={active ? undefined : 'top'}
        withCorner={false}
      >
        <div className={styles.commentContainer} aria-label={ariaLabel}>
          <div className={cn(styles.comment, { [c(styles.commentActive)]: active })} />
        </div>
      </Tooltip>
    </div>
  );
};
